<template>
    <div v-if="accountData">
        <div class="bg-white mb-3">
            <div class="d-flex justify-content-between align-items-center">
                <div>
                    <h1 class="h4 mb-0">{{ accountData.code }} - {{accountData.dentist.first_name}} {{accountData.dentist.surname}} at {{accountData.practice.company_name}}</h1>
                </div>
            </div>
        </div>
        <ul class="nav nav-tabs">
            <li class="nav-item">
                <router-link :to="{ name: 'Account', params: { account_id: accountData.id }}" class="nav-link">Transactions</router-link>
            </li>
            <li class="nav-item">
                <router-link :to="{ name: 'Statements', params: { account_id: accountData.id }}" class="nav-link">Statements</router-link>
            </li>
            <li v-if="accountExports" class="nav-item">
                <router-link :to="{ name: 'AccountExports', params: { account_id: accountData.id }}" class="nav-link">Exports</router-link>
            </li>
            <li class="nav-item">
                <router-link :to="{ name: 'CreditRequests', params: { id: accountData.id }}" class="nav-link active">Credit Requests</router-link>
            </li>
        </ul>
        <div class="bg-white pt-4">
            <form class="bg-light p-3" novalidate @submit.prevent="submitFilter">
                <div class="row mb-3">
                    <div class="col-md-4">
                        <DropdownInput
                            label="Reason"
                            v-model="request_reason"
                            :v="v$.request_reason"
                            :options="reasons"
                        />
                    </div>
                    <div class="col-md-4">
                        <DropdownInput
                            label="Status"
                            v-model="status"
                            :v="v$.status"
                            :options="statuses"
                        />
                    </div>
                </div>
                <div class="row">
                    <div class="col-xs-12">
                        <button class="btn btn-primary me-1" type="submit">Submit</button>
                    </div>
                </div>
            </form>
            <br />
            <table v-if="creditRequestsData && creditRequestsData.data.length" class="credit-requests-table">
                <thead>
                    <tr>
                        <th class="column-shrink">Invoice</th>
                        <th class="column-shrink">Requested</th>
                        <th>Reason</th>
                        <th class="column-shrink">Status</th>
                        <th class="column-shrink"></th>
                    </tr>
                </thead>
                <tbody v-for="creditRequest of creditRequestsData.data" v-bind:key="creditRequest.id" class="mb-1">
                    <tr>
                        <td>#{{ creditRequest.invoice_id }}</td>
                        <td>{{ $filters.formatDate(creditRequest.created_at) }}</td>
                        <td>{{ creditRequest.request_reason_description }}</td>
                        <td><span class="badge" :class="'bg-' + creditRequest.status_color">{{ creditRequest.status_description }}</span></td>
                        <td class="column-number">
                            <router-link v-if="creditRequest.order_id" class="btn btn-secondary btn-sm" :to="{ name: 'Order', params: { id: creditRequest.order_id }}">Order</router-link>
                            <router-link class="btn btn-secondary btn-sm ms-2" :to="{ name: 'Transaction', params: { account_id: accountData.id, id: creditRequest.invoice_id }}">Invoice</router-link>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div v-if="creditRequestsData && !creditRequestsData.data.length">
                No Credit Requests
            </div>
            <Pagination
                v-if="creditRequestsData && creditRequestsData.data.length"
                v-bind:currentPage="currentPage"
                v-bind:lastPage="lastPage"
                viewComponent="CreditRequests"
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
    .credit-requests-table
    {
        border-collapse: collapse;
        width: 100%;
        line-height: 1em;

        td
        {
            border-top: 1px solid #EEEEEE;
        }

        td,
        th
        {
            vertical-align: middle;
            padding: 10px 0.75em;

            &:first-child
            {
                padding-left: 0px;
            }

            &:last-child
            {
                padding-right: 0px;
            }
        }

        .column-shrink
        {
            width: 0px;
            white-space: nowrap;
        }

        .column-number
        {
            width: 0px;
            white-space: nowrap;
            text-align: right;
        }
    }
</style>

<script>
    import api from "@/services/api";
    import useVuelidate from '@vuelidate/core';
    import Pagination from '@/components/Pagination.vue';
    import DropdownInput from '@/components/DropdownInput.vue';

    export default {
        setup () {
            return {
                v$: useVuelidate()
            }
        },
        props: {
            account_id: {
                required: true
            }
        },
        components: {
            Pagination,
            DropdownInput,
        },
        validations () {
            return {
                request_reason: { },
                status: { },
            }
        },
        data() {
            return {
                accountData: null,
                creditRequestsData: null,
                accountExports: false,
                reasons: [],
                statuses: [],
                request_reason: '',
                status: '',
            };
        },
        mounted () {
            this.updateFromQuery();
            this.fetchAccount();
            this.fetchReasons();
            this.fetchStatuses();
        },
        computed: {
            currentPage () {
                var pageParam = this.$route.query.page;
                return typeof pageParam === 'undefined' ? 1 : parseInt(pageParam);
            },
            lastPage () {
                if (this.creditRequestsData !== null) {
                    return parseInt(this.creditRequestsData.meta.last_page);
                }

                return null;
            },
        },
        watch: {
            currentPage() {
                this.fetchCreditRequests();
            },
            '$store.state.currentPracticeId': function() {
                this.fetchCreditRequests();
            },
            '$route.query': function() {
                this.updateFromQuery();
                if (this.accountData) {
                    this.fetchCreditRequests();
                }
            },
        },
        methods: {
            fetchAccount: function() {
                api
                .get('/api/v1/accounts/' + this.account_id)
                .then((response) => {
                    console.log(response);
                    this.accountData = response.data.data;
                    this.fetchCreditRequests();
                })
                .catch(() => {
                    console.log('Catch account error');
                });
            },
            fetchReasons: function() {
                api
                .get('/api/v1/credit-requests/reasons')
                .then((response) => {
                    this.reasons = response.data.data;
                })
                .catch(() => {
                    console.log('Catch reasons error');
                });
            },
            fetchStatuses: function() {
                api
                .get('/api/v1/credit-requests/statuses')
                .then((response) => {
                    this.statuses = response.data.data;
                })
                .catch(() => {
                    console.log('Catch statuses error');
                });
            },
            fetchCreditRequests: function() {
                var params = {
                    page: this.currentPage,
                };

                if (String(this.request_reason).length) {
                    params.request_reason = this.request_reason;
                }

                if (String(this.status).length) {
                    params.status = this.status;
                }

                api
                .get('/api/v1/accounts/' + this.account_id + '/credit-requests', { params })
                .then((response) => {
                    console.log('credits');
                    console.log(response)
                    this.creditRequestsData = response.data;
                })
                .catch(() => {
                    console.log('Catch credit requests error');
                });
            },
            submitFilter: async function () {
                var isValid = await this.v$.$validate();

                if (isValid) {
                    var query = {};

                    if (String(this.request_reason).length) {
                        query.request_reason = this.request_reason;
                    }

                    if (String(this.status).length) {
                        query.status = this.status;
                    }

                    this.$router.replace({ name: 'CreditRequests', query: query });
                    this.fetchCreditRequests();
                }

                return false;
            },
            updateFromQuery: function() {
                this.request_reason = '';
                this.status = '';

                if (typeof this.$route.query.request_reason !== 'undefined') {
                    this.request_reason = this.$route.query.request_reason;
                }

                if (typeof this.$route.query.status !== 'undefined') {
                    this.status = this.$route.query.status;
                }
            },
        },
    }
</script>
